import React, { useState, useRef, useEffect } from 'react'
import { Search, Send, Bookmark, Plus, Copy, ExternalLink, FileText, Database, Globe, Bot } from 'lucide-react'
import WorkPanelWrapper from '../common/WorkPanelWrapper'

interface ResearchMessage {
  id: string
  type: 'user' | 'assistant'
  content: string
  timestamp: Date
  sources?: ResearchSource[]
  saved?: boolean
}

interface ResearchSource {
  type: 'document' | 'database' | 'web' | 'case'
  title: string
  url?: string
  snippet?: string
}

interface ResearchViewProps {
  onSaveToNotes?: (content: string) => void
}

const ResearchView: React.FC<ResearchViewProps> = ({ onSaveToNotes }) => {
  const [messages, setMessages] = useState<ResearchMessage[]>([
    {
      id: '1',
      type: 'assistant',
      content: 'Hello! I can help you research information for your cases. Ask me about documents, regulations, calculations, or anything else you need to investigate.',
      timestamp: new Date()
    }
  ])
  const [inputValue, setInputValue] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`
    }
  }, [inputValue])

  const handleSend = async () => {
    if (!inputValue.trim() || isSearching) return

    const userMessage: ResearchMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsSearching(true)

    // Simulate AI response with sources
    setTimeout(() => {
      const assistantMessage: ResearchMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: generateResearchResponse(userMessage.content),
        timestamp: new Date(),
        sources: generateSources(userMessage.content)
      }
      setMessages(prev => [...prev, assistantMessage])
      setIsSearching(false)
    }, 1500)
  }

  const generateResearchResponse = (query: string): string => {
    const lowerQuery = query.toLowerCase()
    
    if (lowerQuery.includes('dti') || lowerQuery.includes('debt')) {
      return "The debt-to-income (DTI) ratio is calculated by dividing total monthly debt payments by gross monthly income. For conventional loans, the maximum DTI is typically 43%, though some programs allow up to 50% with compensating factors. FHA loans generally allow up to 57% with strong credit."
    } else if (lowerQuery.includes('document') || lowerQuery.includes('required')) {
      return "For a mortgage application, the following documents are typically required: W-2 forms (2 years), pay stubs (30 days), bank statements (2-3 months), tax returns (2 years), ID and Social Security card, and proof of any additional income or assets."
    } else if (lowerQuery.includes('credit') || lowerQuery.includes('score')) {
      return "Credit score requirements vary by loan type: Conventional loans typically require 620+, FHA loans accept scores as low as 500 (with 10% down) or 580 (with 3.5% down), VA loans have no official minimum but lenders often require 620+, and USDA loans typically need 640+."
    } else {
      return `I've found relevant information about "${query}". Based on current lending guidelines and regulations, here are the key points to consider. Would you like me to search for more specific details?`
    }
  }

  const generateSources = (query: string): ResearchSource[] => {
    const sources: ResearchSource[] = []
    
    if (query.toLowerCase().includes('maria') || query.toLowerCase().includes('gonzalez')) {
      sources.push({
        type: 'case',
        title: 'Maria Gonzalez - Mortgage Application',
        snippet: 'Active case with 75% completion'
      })
    }
    
    sources.push(
      {
        type: 'document',
        title: 'Lending Guidelines 2025',
        snippet: 'Updated requirements and ratios for mortgage applications'
      },
      {
        type: 'database',
        title: 'Historical Rate Data',
        snippet: 'Current and historical mortgage interest rates'
      },
      {
        type: 'web',
        title: 'CFPB Mortgage Rules',
        url: 'https://www.consumerfinance.gov',
        snippet: 'Official regulatory guidance on mortgage lending'
      }
    )
    
    return sources
  }

  const handleSaveToNotes = (message: ResearchMessage) => {
    // Format the note content nicely
    let noteContent = `📔 Research Finding - ${new Date().toLocaleString()}\n\n`
    noteContent += `${message.content}\n`
    
    if (message.sources && message.sources.length > 0) {
      noteContent += `\n📌 Sources:\n`
      message.sources.forEach(source => {
        noteContent += `• ${source.title}`
        if (source.snippet) {
          noteContent += ` - ${source.snippet}`
        }
        noteContent += '\n'
      })
    }
    
    if (onSaveToNotes) {
      onSaveToNotes(noteContent)
      
      // Show visual feedback that it was saved
      setMessages(prev => prev.map(m => 
        m.id === message.id ? { ...m, saved: true } : m
      ))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const getSourceIcon = (type: ResearchSource['type']) => {
    switch (type) {
      case 'document': return <FileText className="w-4 h-4" />
      case 'database': return <Database className="w-4 h-4" />
      case 'web': return <Globe className="w-4 h-4" />
      case 'case': return <FileText className="w-4 h-4" />
      default: return <FileText className="w-4 h-4" />
    }
  }

  // Quick search suggestions
  const quickSearches = [
    'DTI calculation requirements',
    'Required documents checklist',
    'Credit score minimums',
    'Income verification rules',
    'Asset documentation',
    'Gift letter requirements'
  ]

  return (
    <WorkPanelWrapper title="Research Assistant">
      <div className="flex flex-col h-full" style={{ height: 'calc(100vh - 280px)' }}>
        {/* Quick Search Suggestions */}
        <div className="flex-shrink-0 mb-4">
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-gray-500">Quick searches:</span>
            <div className="flex flex-wrap gap-2">
              {quickSearches.slice(0, 3).map((search, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputValue(search)}
                  className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs text-gray-700 transition-colors"
                >
                  {search}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto space-y-4 mb-4 min-h-0">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-2xl ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                <div className="flex items-start space-x-2">
                  {message.type === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <Bot className="w-5 h-5 text-orange-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div
                      className={`px-4 py-3 rounded-lg ${
                        message.type === 'user'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    
                    {/* Sources */}
                    {message.sources && message.sources.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="text-xs text-gray-500 font-medium">Sources:</div>
                        {message.sources.map((source, idx) => (
                          <div
                            key={idx}
                            className="flex items-start space-x-2 p-2 bg-white border border-gray-200 rounded text-xs hover:bg-gray-50 transition-colors"
                          >
                            <span className="flex-shrink-0 text-gray-400 mt-0.5">
                              {getSourceIcon(source.type)}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-700">{source.title}</div>
                              {source.snippet && (
                                <div className="text-gray-500 mt-0.5">{source.snippet}</div>
                              )}
                            </div>
                            {source.url && (
                              <a
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0 text-blue-500 hover:text-blue-600"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    {message.type === 'assistant' && (
                      <div className="flex items-center space-x-2 mt-2">
                        <button
                          onClick={() => handleSaveToNotes(message)}
                          className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-all transform ${
                            message.saved
                              ? 'bg-green-100 text-green-700 scale-105'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:scale-105'
                          }`}
                          disabled={message.saved}
                        >
                          {message.saved ? (
                            <>
                              <Bookmark className="w-3 h-3 fill-current" />
                              <span>Saved to Notes ✓</span>
                            </>
                          ) : (
                            <>
                              <Plus className="w-3 h-3" />
                              <span>Save to Notes</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => navigator.clipboard.writeText(message.content)}
                          className="flex items-center space-x-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs text-gray-700 transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          <span>Copy</span>
                        </button>
                      </div>
                    )}

                    <div className="text-xs text-gray-400 mt-1">
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {isSearching && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-orange-600" />
                </div>
                <div className="px-4 py-3 bg-gray-100 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 border-t border-gray-200 pt-4">
          <div className="flex items-end space-x-2">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about regulations, documents, calculations, or search case information..."
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none"
                style={{ minHeight: '40px', maxHeight: '120px' }}
                disabled={isSearching}
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isSearching}
                className={`absolute right-2 bottom-2 p-1.5 rounded-lg transition-colors ${
                  inputValue.trim() && !isSearching
                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSearching ? (
                  <Search className="w-4 h-4 animate-pulse" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
            <span>Press Enter to send, Shift+Enter for new line</span>
            <span>Research across documents, cases, and regulations</span>
          </div>
        </div>
      </div>
    </WorkPanelWrapper>
  )
}

export default ResearchView
export type { ResearchMessage, ResearchSource }
